import { LoadingGenericBase } from '@instacart/ids-customers'
import { InputBase } from '@instacart/ids-tooling'
import {
  FormField,
  FormFieldLabel,
  ModalBody,
  ModalFooter,
  ModalHeader,
  spacing,
} from '@retailer-platform/shared-components'
import type { PropsWithChildren, FunctionComponent } from 'react'
import { Fragment, useCallback } from 'react'
import { Button } from '@retailer-platform/shared-components/src/tds'
import { useCurrentAccountContext } from '@retailer-platform/dashboard/utils'
import { useDomainMessages } from '../../utils/domain/intl'
import { useGetCurrentAccountUsesPasswordQuery } from '../../api'
import { passwordResetLink } from '../../utils/accountManagementUtils'
import { PostLoginLocaleSelect } from './PostLoginLocaleSelect'

type AccountManagementMyAccountFormProps = {}

export const AccountManagementMyAccountForm: FunctionComponent<
  PropsWithChildren<AccountManagementMyAccountFormProps>
> = () => {
  const authUserDataResult = useGetCurrentAccountUsesPasswordQuery()
  const currentEnterpriseAccount = authUserDataResult.data?.currentEnterpriseAccount

  const roles = currentEnterpriseAccount?.roles
  const isInternal = currentEnterpriseAccount?.isInternal

  const messages = useDomainMessages({
    title: 'accountManagementDomain.myAccount.title',
    name: 'accountManagementDomain.myAccount.form.name',
    email: 'accountManagementDomain.myAccount.form.email',
    roles: 'accountManagementDomain.myAccount.form.roles',
    language: 'accountManagementDomain.myAccount.form.language',
    updatePassword: 'accountManagementDomain.myAccount.form.updatePassword',
  })

  const { account } = useCurrentAccountContext()
  const usesPassword = currentEnterpriseAccount?.authUserData?.usesPassword ?? false

  const handlePasswordReset = useCallback(() => {
    const resetUrl = passwordResetLink({
      authService: usesPassword,
      email: account.email,
    })
    window.open(resetUrl, '_blank')
  }, [usesPassword, account.email])

  if (authUserDataResult.loading)
    return (
      <div css={{ position: 'relative', height: 500 }}>
        <LoadingGenericBase />
      </div>
    )

  return (
    <Fragment>
      <ModalHeader>{messages.title}</ModalHeader>

      <ModalBody>
        <div
          css={{
            display: 'flex',
            flexDirection: 'column',
            gap: spacing.X24,
          }}
        >
          <FormField noMargin>
            <FormFieldLabel>{messages.name}</FormFieldLabel>
            <InputBase id="name" disabled value={account.givenName} />
          </FormField>

          <FormField noMargin>
            <FormFieldLabel>{messages.email}</FormFieldLabel>
            <InputBase id="email" disabled value={account.email} />
          </FormField>

          {roles && isInternal && (
            <FormField noMargin>
              <FormFieldLabel>{messages.roles}</FormFieldLabel>
              {roles.map(r => r.name).join(', ')}
            </FormField>
          )}

          <FormField noMargin>
            <FormFieldLabel>{messages.language}</FormFieldLabel>
            <PostLoginLocaleSelect />
          </FormField>
        </div>
      </ModalBody>

      <ModalFooter>
        <div
          css={{
            display: 'flex',
            justifyContent: 'flex-end',
            width: '100%',
          }}
        >
          {usesPassword && (
            <Button
              data-testid="myAccount-reset-password"
              variant="primary"
              type="button"
              onClick={handlePasswordReset}
            >
              {messages.updatePassword}
            </Button>
          )}
        </div>
      </ModalFooter>
    </Fragment>
  )
}
